import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Award, Keyboard, LogOut, Shield, DollarSign, ChevronRight, Loader2 } from 'lucide-react';

export default function AccountPage() {
  const { user, logout, refreshUser } = useAuth();
  const [testActive, setTestActive] = useState(false);
  const [sampleText, setSampleText] = useState('');
  const [typedText, setTypedText] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const startTypingTest = async () => {
    try {
      const { data } = await api.get('/typing-test/sample');
      setSampleText(data.sample_text);
      setTypedText('');
      setStartTime(null);
      setTestResult(null);
      setTestActive(true);
    } catch (err) {
      toast.error('Failed to load typing test');
    }
  };

  const handleType = (e) => {
    const val = e.target.value;
    if (!startTime && val.length === 1) {
      setStartTime(Date.now());
    }
    setTypedText(val);
  };

  const submitTest = async () => {
    if (!startTime || !typedText.trim()) return;
    const elapsed = (Date.now() - startTime) / 1000;
    setSubmitting(true);
    try {
      const { data } = await api.post('/typing-test/submit', {
        original_text: sampleText,
        typed_text: typedText,
        time_seconds: elapsed,
      });
      setTestResult(data);
      setTestActive(false);
      toast.success(`Test complete! ${data.wpm} WPM, ${data.accuracy}% accuracy`);
      await refreshUser();
    } catch (err) {
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const roleLabel = {
    user: 'Regular User',
    transcriber: 'Verified Transcriber',
    admin: 'Administrator',
  };

  const roleBadge = {
    user: 'bg-[var(--brand-border)] text-[var(--brand-text-sec)]',
    transcriber: 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] border-[var(--brand-accent)]/20',
    admin: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20',
  };

  return (
    <div className="pb-nav" data-testid="account-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--brand-text)] mb-6">
          Account
        </h1>

        {/* Profile Card */}
        <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="profile-card">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-[var(--brand-primary)]/10 flex items-center justify-center flex-shrink-0">
              <User size={24} className="text-[var(--brand-primary)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold tracking-tight text-[var(--brand-text)]">{user?.name}</h2>
              <p className="text-sm text-[var(--brand-text-sec)] truncate">{user?.email}</p>
              <Badge className={`mt-2 rounded-sm text-[10px] ${roleBadge[user?.role] || roleBadge.user}`}>
                {roleLabel[user?.role] || 'User'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Balance */}
        <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="balance-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign size={20} className="text-[var(--brand-accent)]" />
              <span className="text-sm font-semibold text-[var(--brand-text)]">Balance</span>
            </div>
            <span className="text-3xl font-black font-mono text-[var(--brand-primary)]" data-testid="balance-amount">
              ${(user?.balance || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Typing Test Stats */}
        {(user?.typing_test_wpm > 0) && (
          <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="typing-stats">
            <div className="flex items-center gap-3 mb-3">
              <Keyboard size={20} className="text-[var(--brand-text-sec)]" />
              <span className="text-sm font-semibold text-[var(--brand-text)]">Typing Test Results</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[var(--brand-text-sec)] uppercase tracking-wider">Speed</p>
                <p className="text-2xl font-black font-mono text-[var(--brand-text)]">{user.typing_test_wpm} <span className="text-sm font-normal">WPM</span></p>
              </div>
              <div>
                <p className="text-xs text-[var(--brand-text-sec)] uppercase tracking-wider">Accuracy</p>
                <p className="text-2xl font-black font-mono text-[var(--brand-text)]">{user.typing_test_accuracy}<span className="text-sm font-normal">%</span></p>
              </div>
            </div>
          </div>
        )}

        {/* Transcriber Application */}
        {user?.role === 'user' && !user?.transcriber_applied && (
          <div className="bg-white border border-[var(--brand-border)] p-6 mb-4" data-testid="transcriber-cta">
            <div className="flex items-center gap-3 mb-3">
              <Award size={20} className="text-[var(--brand-primary)]" />
              <span className="text-sm font-semibold text-[var(--brand-text)]">Become a Transcriber</span>
            </div>
            <p className="text-sm text-[var(--brand-text-sec)] mb-4">
              Take a typing test to prove your skills and earn money by digitizing documents.
            </p>
            {!testActive ? (
              <button
                data-testid="start-typing-test"
                onClick={startTypingTest}
                className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2"
              >
                <Keyboard size={18} />
                Take Typing Test
              </button>
            ) : (
              <div className="space-y-4">
                <div className="bg-[var(--brand-bg)] border border-[var(--brand-border)] p-4">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[var(--brand-text-sec)] mb-2">
                    Type the following text:
                  </p>
                  <p className="text-sm leading-relaxed text-[var(--brand-text)]" data-testid="sample-text">
                    {sampleText}
                  </p>
                </div>
                <textarea
                  ref={textareaRef}
                  data-testid="typing-test-input"
                  value={typedText}
                  onChange={handleType}
                  placeholder="Start typing here..."
                  rows={5}
                  className="w-full rounded-sm border border-[var(--brand-border)] bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]"
                  autoFocus
                />
                <button
                  data-testid="submit-typing-test"
                  onClick={submitTest}
                  disabled={submitting || !typedText.trim()}
                  className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                  {submitting ? 'Submitting...' : 'Submit Test'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pending approval notice */}
        {user?.transcriber_applied && !user?.is_transcriber_approved && (
          <div className="bg-[var(--brand-warning)]/10 border border-[var(--brand-warning)]/20 p-4 mb-4 text-sm text-[var(--brand-warning)]" data-testid="pending-approval">
            Your transcriber application is pending admin approval.
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className="bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/20 p-4 mb-4" data-testid="test-result">
            <p className="text-sm font-semibold text-[var(--brand-accent)]">Typing Test Complete!</p>
            <p className="text-sm text-[var(--brand-text)] mt-1">
              Speed: <span className="font-mono font-bold">{testResult.wpm} WPM</span> | 
              Accuracy: <span className="font-mono font-bold">{testResult.accuracy}%</span>
            </p>
          </div>
        )}

        {/* Admin link */}
        {user?.role === 'admin' && (
          <button
            data-testid="admin-link"
            onClick={() => window.location.href = '/admin'}
            className="w-full bg-white border border-[var(--brand-border)] p-4 mb-4 flex items-center justify-between hover:bg-[var(--brand-bg)] transition-colors"
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-[var(--brand-primary)]" />
              <span className="text-sm font-semibold text-[var(--brand-text)]">Admin Dashboard</span>
            </div>
            <ChevronRight size={18} className="text-[var(--brand-text-sec)]" />
          </button>
        )}

        {/* Logout */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full bg-white border border-[var(--brand-border)] p-4 flex items-center gap-3 hover:bg-[var(--brand-bg)] transition-colors text-[var(--brand-danger)]"
        >
          <LogOut size={20} />
          <span className="text-sm font-semibold">Sign Out</span>
        </button>
      </div>
    </div>
  );
}
